/**
 * A step's result fields as a tree to browse: top-level fields first, a field's own fields under it
 * once it is opened, a list's first row under the list. A search flattens it to the matching paths.
 * Fields that are wired always stay in view, so their wires have somewhere to start.
 */

export interface OutputRow {
	path: string;
	/** What the row shows: the field's own name in the tree, the whole path in a search. */
	label: string;
	depth: number;
	hasChildren: boolean;
}

const parentOf = (path: string): string => path.slice(0, Math.max(0, path.lastIndexOf('.')));

const ancestorsOf = (path: string): string[] => {
	const parts = path.split('.');
	return parts.slice(0, -1).map((_, index) => parts.slice(0, index + 1).join('.'));
};

/** Every field above the given ones, so opening them all brings those fields into view. */
export const ancestorsOfAll = (paths: string[]): string[] => [...new Set(paths.flatMap(ancestorsOf))];

export const treeRows = (paths: string[], open: Set<string>): OutputRow[] => {
	const parents = new Set(paths.map(parentOf));
	return paths
		.filter((path) => ancestorsOf(path).every((ancestor) => open.has(ancestor)))
		.map((path) => ({
			path,
			label: path.slice(path.lastIndexOf('.') + 1),
			depth: path.split('.').length - 1,
			hasChildren: parents.has(path)
		}));
};

export const searchRows = (paths: string[], query: string, keep: string[]): OutputRow[] => {
	const needle = query.trim().toLowerCase();
	return paths
		.filter((path) => keep.includes(path) || path.toLowerCase().includes(needle))
		.map((path) => ({ path, label: path, depth: 0, hasChildren: false }));
};
